import { useCallback, useRef, useState } from 'react';
import { setShowMessage } from '../redux/reducer/content';
import { useDispatch } from 'react-redux';
import { useTranslation } from '../translations';

export const useCreateImg = () => {
  const canvasData = useRef();
  const [isPending, setIsPending] = useState(false);
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const createImg = useCallback(
    async ({ imagesSrc = [], options = {}, returnType = { type: 'download', params: {} }, extraCallback }) => {
      setIsPending(true);

      const setOptions = (canvas) => {
        for (const key in options) canvas[key] = options[key];
      };

      if (!canvasData.current) {
        const canvas = document.createElement('canvas');

        setOptions(canvas);

        const ctx = canvas.getContext('2d');

        canvasData.current = { ctx, canvas };
      } else {
        const { ctx, canvas } = canvasData.current;

        ctx.clearRect(0, 0, canvas.width, canvas.height);
        canvas.width = 0;
        canvas.height = 0;

        setOptions(canvas);
      }

      const { ctx, canvas } = canvasData.current;

      const images = await Promise.all(
        imagesSrc?.map(
          (src) =>
            new Promise((res) => {
              const img = new Image();
              img.crossOrigin = 'Anonymous';
              img.src = src;
              img.onload = () => res(img);
            }),
        ),
      );

      images?.forEach?.((img, i) => {
        const { width, height, x, offsetX, y, offsetY, entry } = options?.every?.[i] ?? {}; //todo: добавить смещение по x и y

        const isEntry = entry && [options.width, options.height].every((value) => typeof value === 'number');

        let truthWidth = (() => {
          if (isEntry) return entry === 'width' ? options.width : (img.width / img.height) * options.height;

          return width ?? options?.width ?? img?.width;
        })();

        let truthHeight = (() => {
          if (isEntry) return entry === 'height' ? options.height : (img.height / img.width) * options.width;

          return height ?? options?.height ?? img?.height;
        })();

        const truthX = !isEntry ? x ?? 0 : (options.width - truthWidth) / 2;

        const truthY = !isEntry ? y ?? 0 : (options.height - truthHeight) / 2;

        if (!options?.width && typeof options?.width !== 'number' && truthWidth > canvas.width)
          canvas.width = truthWidth;

        if (!options?.height && typeof options?.height !== 'number' && truthHeight > canvas.height)
          canvas.height = truthHeight;

        ctx.drawImage(img, truthX, truthY, truthWidth, truthHeight);
      });

      if (typeof extraCallback === 'function') await extraCallback?.({ ctx, canvas });

      const typeLogic = {
        download: (params) =>
          new Promise((res) => {
            const link = document.createElement('a');
            link.href = canvas.toDataURL(params?.fileExtension ?? 'image/png', params?.quality ?? 1);
            link.download = params?.fileName ?? 'image.png';
            link.click();
            res({ canvas, ctx });
          }),

        base64: (params) =>
          new Promise((res) => {
            const base64 = canvas.toDataURL(params?.fileExtension ?? 'image/png', params?.quality ?? 1);
            res({ base64, canvas, ctx });
          }),

        urlDownload: (params) =>
          new Promise((res) => {
            const link = document.createElement('a');
            link.href = canvas.toDataURL(params?.fileExtension ?? 'image/png', params?.quality ?? 1);
            link.download = params?.fileName ?? 'image.png';
            link.doDownload = link.click?.bind(link);
            res({ download: link.doDownload, link: link.href, canvas, ctx });
          }),

        blobUrl: (params) =>
          new Promise((res) => {
            canvas.toBlob((blob) => res({ blob, canvas, ctx }), params?.blobType ?? 'image/png', params?.quality ?? 1);
          }),

        share: (params) =>
          new Promise((res) => {
            const shareFunction = async (blob) => {
              try {
                const copy = async () => {
                  const { state } = await navigator?.permissions?.query?.({
                    name: 'clipboard-write',
                  });

                  if (state === 'granted') {
                    const clipboardItem = new ClipboardItem({ [blob.type]: blob });
                    await navigator.clipboard.write([clipboardItem]);
                    dispatch(setShowMessage(t('other.imageCopied')));
                    setTimeout(() => dispatch(setShowMessage(false)), 3000);
                    return;
                  }

                  dispatch(setShowMessage(t('other.imageCopyError')));
                  setTimeout(() => dispatch(setShowMessage(false)), 3000);
                };

                const file = new File([blob], params?.shareType ?? 'image.png', { type: blob?.type });

                if (navigator?.canShare?.({ files: [file] })) await navigator?.share?.({ files: [file] });
                else await copy();

                res({ canvas, ctx });
              } catch (e) {
                res({ canvas, ctx });
              }
            };

            try {
              canvas.toBlob(shareFunction, params?.blobType ?? 'image/png', params?.quality ?? 1);
            } catch (e) {
              res({ canvas, ctx });
            }
          }),
      };

      const data = await (typeLogic[returnType?.type ?? 'download'] ?? Promise.resolve)(returnType?.params ?? {});

      setIsPending(false);

      return data;
    },
    [canvasData.current],
  );

  return { createImg, isPending };
};
